import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PageTitle } from '@/layout/page-title';
import { cn } from '@/lib/utils';
import { createFileRoute, Link, useLoaderData } from '@tanstack/react-router'
import { ArrowLeft, CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod"
import toast from 'react-hot-toast';
import { LoadingSpinner } from '@/components/ui/spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getTasksProps, postCreateActivity } from '@/tasks';
import { requireAuth } from '@/auth/requireAuth';
import { CompanySelector } from '@/components/company/company-selector';
import type { Company } from '@/company';
import { DatePickerWithPresets } from '@/components/tasks/date-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';

export const Route = createFileRoute('/(app)/_layout/tasks/create')({
  beforeLoad: requireAuth,
  component: RouteComponent,
  loader: async () => {
    const { data, error } = await getTasksProps();
    return { data, error }
  }
})

function RouteComponent() {
  const [openScope, setOpenScope] = useState(false);
  const [openDescription, setOpenDescription] = useState(false);
  const [openRole, setOpenRole] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data, error: _ } = useLoaderData({ from: '/(app)/_layout/tasks/create' });
  const formSchema = z.object({
    scope: z.string().nonempty("Scope is required"),
    description: z.string().nonempty("Description is required"),
    role: z.string().nonempty("Role is required"),
    companyId: z.string().nullable(),
    deadline: z.date(),
    reminder1: z.date(),
    reminder2: z.date(),
    keyDeadline: z.boolean(),
    year: z.number().min(new Date().getFullYear()),
    month: z.number().min(1).max(12),
  })

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      scope: '',
      description: '',
      role: '',
      companyId: null,
      deadline: new Date(),
      reminder1: undefined,
      reminder2: undefined,
      keyDeadline: false,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
    },
    mode: 'onChange',

  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setLoading(true)
    const data = {
      scope: values.scope,
      description: values.description,
      role: values.role,
      companyId: values.companyId,
      deadline: values.deadline.toISOString(),
      reminder1: values.reminder1 ? values.reminder1.toISOString() : null,
      reminder2: values.reminder2 ? values.reminder2.toISOString() : null,
      keyDeadline: values.keyDeadline,
      closingYear: values.year,
      closingMonth: values.month,
    }
    const { data: _, error } = await postCreateActivity(data);
    setLoading(false)
    if (error) {
      toast.error("Error creating task: " + error);
      return;
    }
    toast.success("Task created successfully!");
  }

  return (
    <div className="container max-w-[1900px] mx-auto py-10 px-4">
      <div className="space-y-4">
        <PageTitle title={`New Task`}
          leftActions={
            <Button asChild variant={'ghost'}>
              <Link to="/manage-tasks" className="text-sm">
                <ArrowLeft className="mr-2" />
                Back to Manage Tasks
              </Link>
            </Button>
          }
        />

        {/* You can add more user details here */}
        <Card>
          <CardHeader>

            <CardTitle className="text-xl font-semibold">Task Information</CardTitle>
          </CardHeader>
          <CardContent>

            <Form {...form} >
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="grid md:grid-cols-3 grid-cols-1 gap-4">

                  <div className="flex gap-2">
                    <FormField
                      control={form.control}
                      name="year"
                      render={({ field }) => (
                        <FormItem
                        className="max-w-48"
                        >
                          <FormLabel>Closing year</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={new Date().getFullYear()}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="month"
                      render={({ field }) => (
                        <FormItem >
                          <FormLabel>Closing month</FormLabel>
                          <FormControl>
                            <Select onValueChange={value => field.onChange(parseInt(value))} defaultValue={field.value?.toString()}>
                              <SelectTrigger className="w-full max-w-48">
                                <SelectValue placeholder="Select a month" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Months</SelectLabel>
                                  {
                                    Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                      <SelectItem key={month} value={month.toString()}>
                                        {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                                      </SelectItem>
                                    ))
                                  }
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="scope"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Scope</FormLabel>
                          <FormControl>
                            <Popover open={openScope} onOpenChange={setOpenScope}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openScope}
                                  className="w-full h-8 justify-between overflow-hidden"
                                >
                                  {field.value ? field.value : "Select or enter scope..."}
                                  <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[300px] p-0">
                                <Command>
                                  <CommandInput
                                    placeholder="Search or type scope..."
                                    value={field.value}
                                    onValueChange={field.onChange}
                                    // Allow free text entry
                                    onKeyDown={e => {
                                      if (e.key === "Enter") {
                                        setOpenScope(false);
                                      }
                                    }}
                                  />
                                  <CommandList>
                                    <CommandEmpty>No scope found.</CommandEmpty>
                                    <CommandGroup>
                                      {data!.scopes.map((scope: string) => (
                                        <CommandItem
                                          key={scope}
                                          value={scope}
                                          onSelect={() => {
                                            field.onChange(scope);
                                            setOpenScope(false);
                                          }}
                                        >
                                          <CheckIcon
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              field.value === scope ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {scope}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                    {/* Option to use free text if not matched */}
                                    {field.value &&
                                      !data!.scopes.includes(field.value) && (
                                        <CommandGroup>
                                          <CommandItem
                                            value={field.value}
                                            onSelect={() => {
                                              setOpenScope(false);
                                            }}
                                          >
                                            <CheckIcon
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                "opacity-100"
                                              )}
                                            />
                                            Use "{field.value}" as custom scope
                                          </CommandItem>
                                        </CommandGroup>
                                      )}
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div>
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Popover open={openDescription} onOpenChange={setOpenDescription}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openDescription}
                                  className="w-full h-8 justify-between overflow-hidden"
                                >
                                  {field.value ? field.value
                                    : "Select or enter description..."}
                                  <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[300px] p-0">
                                <Command>
                                  <CommandInput
                                    placeholder="Search or type description..."
                                    value={field.value}
                                    onValueChange={field.onChange}
                                    onKeyDown={e => {
                                      if (e.key === "Enter") {
                                        setOpenDescription(false);
                                      }
                                    }}
                                  />
                                  <CommandList>
                                    <CommandEmpty>No description found.</CommandEmpty>
                                    <CommandGroup>
                                      {data!.descriptions.map((description: string) => (
                                        <CommandItem
                                          key={description}
                                          value={description}
                                          onSelect={() => {
                                            field.onChange(description)
                                            setOpenDescription(false)
                                          }}
                                        >
                                          <CheckIcon
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              field.value === description ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {description}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                    {field.value &&
                                      !data!.descriptions.includes(field.value) && (
                                        <CommandGroup>
                                          <CommandItem
                                            value={field.value}
                                            onSelect={() => {
                                              setOpenDescription(false);
                                            }}
                                          >
                                            <CheckIcon
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                "opacity-100"
                                              )}
                                            />
                                            Use "{field.value}" as custom description
                                          </CommandItem>
                                        </CommandGroup>
                                      )}
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Role</FormLabel>
                          <FormControl>
                            <Popover open={openRole} onOpenChange={setOpenRole}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openRole}
                                  className="w-full h-8 justify-between overflow-hidden"
                                >
                                  {field.value ? field.value : "Select or enter role..."}
                                  <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[300px] p-0">
                                <Command>
                                  <CommandInput
                                    placeholder="Search or type role..."
                                    value={field.value}
                                    onValueChange={field.onChange}
                                    onKeyDown={e => {
                                      if (e.key === "Enter") {
                                        setOpenRole(false);
                                      }
                                    }}
                                  />
                                  <CommandList>
                                    <CommandEmpty>No role found.</CommandEmpty>
                                    <CommandGroup>
                                      {data!.roles.map((role: string) => (
                                        <CommandItem
                                          key={role}
                                          value={role}
                                          onSelect={() => {
                                            field.onChange(role);
                                            setOpenRole(false);
                                          }}
                                        >
                                          <CheckIcon
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              field.value === role ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {role}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                    {field.value &&
                                      !data!.roles.includes(field.value) && (
                                        <CommandGroup>
                                          <CommandItem
                                            value={field.value}
                                            onSelect={() => {
                                              setOpenRole(false);
                                            }}
                                          >
                                            <CheckIcon
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                "opacity-100"
                                              )}
                                            />
                                            Use "{field.value}" as custom role
                                          </CommandItem>
                                        </CommandGroup>
                                      )}
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div >

                    <FormField
                      control={form.control}
                      name="companyId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company</FormLabel>
                          <FormControl>
                            <div className="w-full">

                              <CompanySelector
                                companies={data!.companies}
                                onSelectCompany={(company: Company | null) => field.onChange(company?.id)}
                              />
                            </div>
                          </FormControl>
                          {/* <FormDescription>Select the manager's email from the list.</FormDescription> */}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>

                    <FormField
                      control={form.control}
                      name="deadline"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Deadline</FormLabel>
                          <FormControl>
                            <DatePickerWithPresets
                              value={field.value}

                              onChange={(date) => field.onChange(date)}
                            />
                          </FormControl>
                          <FormDescription>Select the deadline for the task.</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>

                    <FormField
                      control={form.control}
                      name="reminder1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reminder 1</FormLabel>
                          <FormControl>
                            <DatePickerWithPresets
                              value={field.value}
                              onChange={(date) => field.onChange(date)}
                            />
                          </FormControl>
                          <FormDescription>Set the first reminder for the task.</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>

                    <FormField
                      control={form.control}
                      name="reminder2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reminder 2</FormLabel>
                          <FormControl>
                            <DatePickerWithPresets
                              value={field.value}
                              onChange={(date) => field.onChange(date)}
                            />
                          </FormControl>
                          <FormDescription>Set the second reminder for the task.</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="keyDeadline"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>
                              Key Deadline
                            </FormLabel>
                            <FormDescription>
                              Mark this task as a key deadline.
                            </FormDescription>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Button
                  disabled={loading}
                  type="submit"
                  className="mt-4"
                >
                  Create
                  {loading && <LoadingSpinner />}
                </Button>
              </form>
            </Form>
          </CardContent>

        </Card>
      </div>
    </div>
  )
}
